import { Button } from "@progress/kendo-react-buttons";
// @ts-ignore
import Export from "@/components/Export";
import SearchBox from "@/components/SearchBox";
import {
  useAllCompanies,
  useCompanies,
  useUpdateCompany,
} from "@/hooks/useCompanies";
import { RootState } from "@/store/store";
import { buildFilters, companyFilters, getPaginationData } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { columnProps } from "@/components/ColumnMenu";
import { queryKeys } from "@/constants/queryKeyFactory";
import { useSearchFilter } from "@/hooks/useSearchFilters";
import { queryClient } from "@/lib/queryClient";
import { companySvc } from "@/services/company.service";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  CheckboxChangeEvent,
  Switch,
  TextAreaChangeEvent,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Plus } from "lucide-react";
import { createRef, useEffect, useMemo, useState } from "react";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { useAppSelector } from "../../store/hooks/hooks";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";
import { ICompany } from "@/interfaces/company.interface";
import {
  ComboBoxChangeEvent,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";

let mappingChanged = false;

export const Listing = () => {
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();
  const updateCompanyMutation = useUpdateCompany();

  const [addNewCompany, setAddNewCompany] = useState<
    (ICompany & { id?: string; imageFile?: File }) | null
  >(null);
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showDrawer, setShowDrawer] = useState(false);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableCompany,
      selectedProject?.id
    )
  );
  const [showArchived, setShowArchived] = useState(false);

  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["name"],
    });

  const filterParams = useMemo(
    () => ({
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort ?? null,
      filter: companyFilters(
        "company",
        selectedProject?.id,
        dataState.filter,
        showArchived
      ),
    }),
    [
      page.skip,
      page.take,
      dataState.sort,
      selectedProject?.id,
      dataState.filter,
      showArchived,
    ]
  );

  const { data: companiesData, refetch } = useCompanies(filterParams, {
    enabled: !!filterParams,
  });

  const { data: allCompaniesData, refetch: refetchAllCompanies } =
    useAllCompanies(
      { filter: buildFilters(selectedProject?.id) },
      { enabled: false }
    );

  const excelExport = () => {
    if (_exporter.current) {
      if (companiesData?.data && companiesData.data.length > 0) {
        // Get the formatted data

        // Save with the formatted data
        _exporter.current.save(companiesData?.data);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            _exporter.current?.save(companiesData?.data || []);
          }, 300);
        });
      }
    }
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      enableDisableCompany,
      selectedProject?.id
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? companies.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleChange = (
    e:
      | TextBoxChangeEvent
      | ComboBoxChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | MultiSelectChangeEvent
  ) => {
    if (e.target.name === "id") {
      setAddNewCompany(e.value);
      return;
    }

    if (e.target.name === "activities") {
      setAddNewCompany((prev: any) => {
        return { ...prev, activities: e.value?.map((item: any) => item.id) };
      });
      return;
    }

    setAddNewCompany((prev: any) => {
      return { ...prev, [e.target.name as any]: e.target.value };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const createCompany = () => {
    if (!addNewCompany) return;

    const companyData = {
      ...addNewCompany,
      projects: selectedProject?.id ? [{ id: selectedProject.id, isActive: true }] : [],
    };

    companySvc.createCompany(companyData, companyData.imageFile).then((res) => {
      if (!res?.error) {
        ToastService.success("Company created successfully");
        queryClient.invalidateQueries({ queryKey: queryKeys.company.root });
        setShowDrawer(false);
        return;
      }

      ToastService.error(res?.error?.message || "Something went wrong");
    });
  };

  const editCompany = async () => {
    if (!addNewCompany?.id) return;
    const companyData = { ...addNewCompany };
    if (addNewCompany.imageFile) {
      const res = await companySvc.uploadImage(
        addNewCompany.id,
        addNewCompany.imageFile
      );

      if (!res?.data) {
        ToastService.error("Failed to upload image");
        return;
      }

      companyData.imageUrl = res.data;
    }

    if (!companyData?.projects || companyData.projects.length === 0) {
      companyData.projects = selectedProject?.id
        ? [{ id: selectedProject.id, isActive: true }]
        : [];
    } else {
      const existingMembership = companyData.projects.find(
        (item) => item.id === selectedProject?.id
      );
      if (!existingMembership) {
        companyData.projects = [
          ...companyData.projects,
          { id: selectedProject!.id!, isActive: true },
        ];
      }
    }

    updateCompanyMutation.mutate(
      { id: companyData.id!, data: companyData },
      {
        onSuccess: () => {
          ToastService.success("Company updated successfully");
          setShowDrawer(false);
        },
      }
    );
  };

  function enableDisableCompany(dataItem: ICompany) {
    if (!dataItem?.id || !selectedProject?.id) return;
    const isCurrentlyActive = dataItem.projects?.some(
      (p) => p.id === selectedProject.id && p.isActive
    ) ?? false;
    companySvc.toggleProjectMembership(dataItem.id, selectedProject.id, !isCurrentlyActive)
      .then(() => {
        ToastService.success("Company updated successfully");
        queryClient.invalidateQueries({ queryKey: queryKeys.company.root });
      });
  }

  function handleEditClick(data: ICompany) {
    setAddNewCompany(data);
    setShowDrawer(true);
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewCompany(null);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (companiesData?.data) {
      setCompanies(companiesData.data);
    }
  }, [companiesData]);

  useEffect(() => {
    if (!companies?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableCompany,
      selectedProject?.id
    );
    setStateColumns(cols);
  }, [companies, columnsMapping]);

  useEffect(() => {
    setStateColumns(
      generateColumns(
        handleEditClick,
        onColumnsSubmit,
        null,
        enableDisableCompany,
        selectedProject?.id
      )
    );
  }, [selectedProject?.id]);

  const [isExisting, setIsExisting] = useState(false);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={companies}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={companiesData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => setShowDrawer(true)}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>

              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => {
                  setIsExisting(true);
                  setShowDrawer(true);
                }}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add Existing
                </span>
              </Button>
              <div className="flex items-center gap-2 md:order-3 sm:order-2 order-4 sm:mt-0 mt-2 sm:ml-0 ml-auto">
                <Label className="font-medium text-sm font-cabin">
                  Show Inactive
                </Label>
                <Switch
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.value);
                  }}
                />
              </div>
              <div
                className="cursor-pointer sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allCompaniesData?.data || []}
                  collapsible={true}
                  fileName="Companys.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      {...columnProps(column.field, dataState)}
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    {...columnProps(column.field, dataState)}
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      <WriteDrawer
        isExisting={isExisting}
        setIsExisting={setIsExisting}
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        addNewCompany={addNewCompany}
        handleChange={handleChange}
        onSave={addNewCompany?.id ? editCompany : createCompany}
      />
    </>
  );
};
